/**
 * WallRegionBuilder
 *
 * Boolean-inflate room polygons and union them to recover the building's
 * outer-wall outline plus any interior cavities (fireplaces, courtyards,
 * light wells). The existing skeleton algorithm only sees room polygons,
 * which gives it no anchor for the outer walls — these synthetic boundaries
 * fill that gap.
 */

import * as THREE from 'three';
import polygonClipping, { Ring as PCRing, Polygon as PCPolygon } from 'polygon-clipping';

/** A connected component of the wall region. */
export interface WallRegionComponent {
  /** Outer boundary, CCW (polygon-clipping convention). */
  outerRing: THREE.Vector2[];
  /** Interior holes (cavities), each CW (polygon-clipping convention). */
  holes: THREE.Vector2[][];
}

/** Miter limit: above this ratio (miterLen / t), bevel the corner instead. */
const MITER_LIMIT = 4;

/**
 * Fixed inflation (m) of the HOLE pass: interior cavities depend on how far
 * the rooms inflate (a thick user setting swallows the gaps between rooms),
 * so the holes always come from a run at this reference thickness — only the
 * outer ring follows the user's wall-thickness setting.
 */
const HOLE_REFERENCE_WALL_THICKNESS_M = 0.2;

/**
 * Inflate each room outward by `wallThickness`, union them, and return one
 * component per resulting polygon. Outer rings come back CCW; cavity rings
 * come back CW.
 *
 * The build runs TWICE: the OUTER rings come from the `wallThickness` run,
 * while the interior holes come from a second run at the fixed
 * {@link HOLE_REFERENCE_WALL_THICKNESS_M} — an outer-outline swap. Without
 * it, changing the wall thickness would also grow/shrink (or swallow) the
 * cavities between rooms; pinning the hole pass keeps them consistent no
 * matter the user setting. Each reference hole files into the component
 * whose outer ring contains it.
 */
export function buildWallRegionFromRooms(rooms: Array<{ identifier: string; polygon: THREE.Vector3[] }>, wallThickness: number): WallRegionComponent[] {
  const userComponents = buildWallRegionSinglePass(rooms, wallThickness);
  if (Math.abs(wallThickness - HOLE_REFERENCE_WALL_THICKNESS_M) < 1e-9) return userComponents;
  const referenceHoles = buildWallRegionSinglePass(rooms, HOLE_REFERENCE_WALL_THICKNESS_M).flatMap((c) => c.holes);
  return userComponents.map((component) => ({
    outerRing: component.outerRing,
    holes: referenceHoles.filter((hole) => pointInRing(hole[0], component.outerRing)),
  }));
}

/** One merged-room result: the rooms that ended up connected and the ring they merged into. */
export interface RoomMergeGroup {
  /** Identifiers of the source rooms in this connected group (1 = not mergeable). */
  roomIdentifiers: string[];
  /** Merged boundary, CCW, cleaned. For singleton groups this is the room's own ring. */
  outerRing: THREE.Vector2[];
}

/** Extra bridge reach beyond half the wall thickness, so a wall exactly at
 *  the configured thickness still merges despite scan/detection noise. */
const MERGE_BRIDGE_MARGIN_M = 0.02;
/** How far inside a room the component-assignment probe point sits. */
const MERGE_INTERIOR_NUDGE_M = 0.01;

/**
 * Merge room polygons that are adjacent (separated by at most
 * `maxWallThickness` of wall) into single boundaries.
 *
 * Morphological closing: dilate every room by `maxWallThickness / 2` (plus a
 * small margin), union, erode the result back — that fills the wall strip
 * between adjacent rooms without moving their outer boundaries. The eroded
 * bridges are then unioned with the EXACT original rings so every boundary
 * segment that isn't a swallowed wall stays bit-identical to the source room.
 *
 * Returns one group per connected component; rooms that touch nothing else
 * come back as singleton groups (callers should leave those untouched).
 * Interior holes (courtyards enclosed by the merged rooms) are dropped —
 * the room model cannot represent holes.
 */
export function mergeRoomPolygons(rooms: Array<{ identifier: string; polygon: THREE.Vector3[] }>, maxWallThickness: number): RoomMergeGroup[] {
  const prepped: Array<{ identifier: string; ring: THREE.Vector2[] }> = [];
  for (const r of rooms) {
    const pts2 = r.polygon.map((p) => new THREE.Vector2(p.x, p.y));
    if (pts2.length < 3) continue;
    prepped.push({ identifier: r.identifier, ring: isCCW(pts2) ? pts2 : pts2.slice().reverse() });
  }
  if (prepped.length === 0) return [];

  const bridge = maxWallThickness / 2 + MERGE_BRIDGE_MARGIN_M;

  // Closing pass: dilated union bridges walls up to maxWallThickness wide.
  const dilated: PCPolygon[] = prepped.map((p) => [ringToPCRing(offsetRingOutward(p.ring, bridge))]);
  const closedComponents = robustUnion(dilated);

  // Final union input: the exact room rings + each closed component eroded
  // back to size. The erosion may undercut sharp corners slightly (beveled
  // offsets), but unioning with the originals restores them.
  const finalInput: PCPolygon[] = prepped.map((p) => [ringToPCRing(p.ring)]);
  let droppedHoles = false;
  for (const poly of closedComponents) {
    if (poly.length === 0) continue;
    // Holes of the closed shape are regions the merged rooms enclose
    // (courtyards / light wells). Eroding only the outer ring fills them.
    if (poly.length > 1) droppedHoles = true;
    const outer = pcRingToVec2(poly[0]);
    if (outer.length < 3) continue;
    const outerCCW = isCCW(outer) ? outer : outer.slice().reverse();
    // The inward offset can self-intersect (an edge shorter than `bridge`
    // collapses and its neighbours cross) — polygon-clipping treats such a
    // ring as garbage, so untwist it into simple loops first.
    for (const loop of untwistRing(offsetRingInward(outerCCW, bridge))) {
      finalInput.push([ringToPCRing(loop)]);
    }
  }
  const finalComponents = robustUnion(finalInput);

  const groups: RoomMergeGroup[] = [];
  for (const poly of finalComponents) {
    if (poly.length === 0) continue;
    if (poly.length > 1) droppedHoles = true;
    const outer = pcRingToVec2(poly[0]);
    if (outer.length < 3) continue;
    const outerCCW = isCCW(outer) ? outer : outer.slice().reverse();
    const outerClean = cleanRing(outerCCW);
    if (outerClean.length < 3) continue;
    groups.push({ roomIdentifiers: [], outerRing: outerClean });
  }
  if (droppedHoles) {
    console.warn('mergeRoomPolygons: the merged shape enclosed interior holes; rooms cannot represent holes, so they were filled.');
  }

  // Assign each room to the smallest final component containing a point just
  // inside it (room vertices lie ON the merged boundary, so probe the interior).
  for (const p of prepped) {
    const probe = interiorPointOf(p.ring);
    let bestIndex = -1;
    let bestArea = Infinity;
    for (let i = 0; i < groups.length; i++) {
      if (!pointInRing(probe, groups[i].outerRing)) continue;
      const area = ringArea(groups[i].outerRing);
      if (area < bestArea) {
        bestArea = area;
        bestIndex = i;
      }
    }
    if (bestIndex >= 0) {
      groups[bestIndex].roomIdentifiers.push(p.identifier);
    } else {
      // The union skipped this room (degenerate geometry) — keep it untouched.
      groups.push({ roomIdentifiers: [p.identifier], outerRing: cleanRing(p.ring) });
    }
  }
  return groups.filter((g) => g.roomIdentifiers.length > 0);
}

/** A point strictly inside a CCW ring: the longest edge's midpoint nudged
 *  toward the interior (left of the edge direction). */
function interiorPointOf(ring: THREE.Vector2[]): THREE.Vector2 {
  const n = ring.length;
  let best = 0;
  let bestLen = -1;
  for (let i = 0; i < n; i++) {
    const len = ring[i].distanceTo(ring[(i + 1) % n]);
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  }
  const a = ring[best];
  const b = ring[(best + 1) % n];
  const dx = (b.x - a.x) / bestLen;
  const dy = (b.y - a.y) / bestLen;
  return new THREE.Vector2((a.x + b.x) / 2 - dy * MERGE_INTERIOR_NUDGE_M, (a.y + b.y) / 2 + dx * MERGE_INTERIOR_NUDGE_M);
}

/** Unsigned shoelace area of a ring. */
function ringArea(ring: THREE.Vector2[]): number {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}

/**
 * Offset a CCW ring inward by `t`: run the outward offset on the reversed
 * (CW) ring — its "outward" points into the original interior — and flip the
 * result back to CCW. Convex corners get beveled slightly on the way in; the
 * merge pipeline unions the result with the exact source rings, which
 * restores them. The result can self-intersect where a feature narrower than
 * `2t` collapses — run it through {@link untwistRing} before boolean ops.
 */
function offsetRingInward(ring: THREE.Vector2[], t: number): THREE.Vector2[] {
  return offsetRingOutward(ring.slice().reverse(), t).reverse();
}

/** Loops smaller than this are offset debris, not geometry worth keeping. */
const UNTWIST_MIN_LOOP_AREA_M2 = 1e-4;
/** Safety valve: stop splitting pathological rings after this many cuts. */
const UNTWIST_MAX_SPLITS = 64;

/**
 * Resolve the self-intersections a bevel offset can produce: an edge shorter
 * than the offset distance collapses, its neighbours cross, and the traversal
 * picks up an inverted "bowtie" loop (rendered as a spurious spike). Splits
 * the ring at every proper self-crossing and keeps only the loops that
 * preserved the CCW orientation with a non-trivial area — the inverted (CW)
 * loops are exactly the fold-over artifacts. A clean ring comes back as-is.
 */
function untwistRing(ring: THREE.Vector2[]): THREE.Vector2[][] {
  const out: THREE.Vector2[][] = [];
  const queue: THREE.Vector2[][] = [ring];
  let splits = 0;
  while (queue.length > 0) {
    const r = queue.pop();
    if (!r || r.length < 3) continue;
    const split = splits < UNTWIST_MAX_SPLITS ? splitAtFirstSelfIntersection(r) : null;
    if (split) {
      splits++;
      queue.push(split[0], split[1]);
      continue;
    }
    if (isCCW(r) && ringArea(r) > UNTWIST_MIN_LOOP_AREA_M2) out.push(r);
  }
  return out;
}

/** Split a ring at its first proper self-crossing into the two sub-loops
 *  meeting at the intersection point; null when the ring is simple. */
function splitAtFirstSelfIntersection(ring: THREE.Vector2[]): [THREE.Vector2[], THREE.Vector2[]] | null {
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue; // adjacent through the wrap-around
      const p = properSegmentIntersection(a1, a2, ring[j], ring[(j + 1) % n]);
      if (!p) continue;
      const loop1: THREE.Vector2[] = [p];
      for (let k = i + 1; k <= j; k++) loop1.push(ring[k]);
      const loop2: THREE.Vector2[] = [p];
      for (let k = j + 1; k < n; k++) loop2.push(ring[k]);
      for (let k = 0; k <= i; k++) loop2.push(ring[k]);
      return [loop1, loop2];
    }
  }
  return null;
}

/** Interior-crossing test — returns the intersection point when the segments
 *  properly cross (shared/touching endpoints don't count). */
function properSegmentIntersection(a1: THREE.Vector2, a2: THREE.Vector2, b1: THREE.Vector2, b2: THREE.Vector2): THREE.Vector2 | null {
  const d1x = a2.x - a1.x;
  const d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x;
  const d2y = b2.y - b1.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / denom;
  const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / denom;
  const EPS = 1e-9;
  if (t <= EPS || t >= 1 - EPS || u <= EPS || u >= 1 - EPS) return null;
  return new THREE.Vector2(a1.x + t * d1x, a1.y + t * d1y);
}

/** Ray-cast point-in-ring test (holes sit strictly inside their component's
 *  outer ring, so testing one hole vertex is sufficient for assignment). */
function pointInRing(point: THREE.Vector2, ring: THREE.Vector2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].x;
    const yi = ring[i].y;
    const xj = ring[j].x;
    const yj = ring[j].y;
    if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** One inflate+union run at a single wall thickness. */
function buildWallRegionSinglePass(rooms: Array<{ identifier: string; polygon: THREE.Vector3[] }>, wallThickness: number): WallRegionComponent[] {
  if (rooms.length === 0) return [];

  const inflated: PCRing[][] = [];
  for (const r of rooms) {
    const pts2 = r.polygon.map((p) => new THREE.Vector2(p.x, p.y));
    if (pts2.length < 3) continue;
    const ccwPts = isCCW(pts2) ? pts2 : pts2.slice().reverse();
    const inflatedRing = offsetRingOutward(ccwPts, wallThickness);
    if (inflatedRing.length < 3) continue;
    inflated.push([ringToPCRing(inflatedRing)]);
  }
  if (inflated.length === 0) return [];

  const unioned = robustUnion(inflated);

  const components: WallRegionComponent[] = [];
  for (const poly of unioned) {
    if (poly.length === 0) continue;
    const rawOuter = poly[0];
    if (rawOuter.length < 4) continue;

    const outerPts = pcRingToVec2(rawOuter);
    const outerCCW = isCCW(outerPts) ? outerPts : outerPts.slice().reverse();
    const outerClean = cleanRing(outerCCW);
    if (outerClean.length < 3) continue;

    const holes: THREE.Vector2[][] = [];
    for (let h = 1; h < poly.length; h++) {
      const rawHole = poly[h];
      if (rawHole.length < 4) continue;
      const holePts = pcRingToVec2(rawHole);
      const holeCW = isCCW(holePts) ? holePts.slice().reverse() : holePts;
      const holeClean = cleanRing(holeCW);
      if (holeClean.length < 3) continue;
      holes.push(holeClean);
    }

    components.push({ outerRing: outerClean, holes });
  }
  return components;
}

/**
 * Quantization ladder (metres) for union-failure retries. 0.1 mm is visually
 * identical; even the coarsest 1 cm step stays well under the 3 cm ring
 * cleanup tolerances below, so a snapped result is indistinguishable
 * downstream.
 */
const UNION_SNAP_LADDER_M = [1e-4, 1e-3, 1e-2];

/**
 * Union the inflated room polygons, surviving polygon-clipping's known
 * robustness failure ("Unable to complete output ring …"): its sweep line
 * chokes on near-degenerate input — vertices or edges a fraction of a
 * millimetre apart, which auto-detected room polygons produce routinely, and
 * more rooms means more chances of a bad pair (the crash behind
 * many-rooms-per-floor wall creation failing outright).
 *
 * Strategy: try the exact input first (bit-identical behavior for every floor
 * that already works). On failure, retry on the quantization ladder — snapping
 * to a grid collapses the near-coincident geometry that breaks the sweep. As a
 * last resort, union incrementally and skip the individual room(s) that still
 * fail, so the tool degrades to a slightly incomplete envelope instead of
 * crashing on enable.
 */
function robustUnion(polys: PCPolygon[]): PCPolygon[] {
  try {
    return polygonClipping.union(polys[0], ...polys.slice(1));
  } catch {
    /* fall through to the snap ladder */
  }
  for (const grid of UNION_SNAP_LADDER_M) {
    const snapped = snapPolygons(polys, grid);
    if (snapped.length === 0) return [];
    try {
      const result = polygonClipping.union(snapped[0], ...snapped.slice(1));
      console.warn(`WallRegionBuilder: room union needed ${grid} m coordinate snapping to succeed.`);
      return result;
    } catch {
      /* try the next coarser grid */
    }
  }
  let acc: PCPolygon[] = [];
  let skipped = 0;
  for (const poly of snapPolygons(polys, UNION_SNAP_LADDER_M[UNION_SNAP_LADDER_M.length - 1])) {
    try {
      acc = acc.length === 0 ? polygonClipping.union(poly) : polygonClipping.union(acc, poly);
    } catch {
      skipped++;
    }
  }
  if (skipped > 0) {
    console.error(`WallRegionBuilder: room union skipped ${skipped} room(s) polygon-clipping could not process; the wall envelope is incomplete around them.`);
  }
  return acc;
}

/**
 * Quantize every coordinate to `grid`, dropping the consecutive duplicate
 * vertices and degenerate rings the rounding can create. Rings stay
 * self-closing (polygon-clipping requires it).
 */
function snapPolygons(polys: PCPolygon[], grid: number): PCPolygon[] {
  const out: PCPolygon[] = [];
  for (const poly of polys) {
    const rings: PCRing[] = [];
    for (const ring of poly) {
      const snapped: PCRing = [];
      for (const [x, y] of ring) {
        const sx = Math.round(x / grid) * grid;
        const sy = Math.round(y / grid) * grid;
        const last = snapped[snapped.length - 1];
        if (!last || last[0] !== sx || last[1] !== sy) snapped.push([sx, sy]);
      }
      // The closing vertex deduped away above — re-close, and drop rings the
      // snapping collapsed below a triangle.
      if (snapped.length < 3) continue;
      const first = snapped[0];
      const last = snapped[snapped.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) snapped.push([first[0], first[1]]);
      if (snapped.length >= 4) rings.push(snapped);
    }
    if (rings.length > 0) out.push(rings);
  }
  return out;
}

/** Tolerances for the post-union ring cleanup. */
const CLEANUP_DUPLICATE_TOLERANCE = 0.03; // 3cm
const CLEANUP_COLLINEAR_TOLERANCE = 0.03; // perpendicular distance from middle vertex to prev->next chord

/**
 * Drop near-duplicate vertices and collinear vertices from a ring. Runs a
 * single linear pass; rings produced by polygon-clipping are well-behaved
 * enough that one pass is sufficient.
 */
function cleanRing(ring: THREE.Vector2[]): THREE.Vector2[] {
  if (ring.length < 3) return ring.slice();

  // Pass 1: drop consecutive duplicates (including the wrap-around).
  const dedup: THREE.Vector2[] = [];
  for (const p of ring) {
    if (dedup.length === 0 || dedup[dedup.length - 1].distanceTo(p) >= CLEANUP_DUPLICATE_TOLERANCE) {
      dedup.push(p);
    }
  }
  while (dedup.length >= 2 && dedup[0].distanceTo(dedup[dedup.length - 1]) < CLEANUP_DUPLICATE_TOLERANCE) {
    dedup.pop();
  }
  if (dedup.length < 3) return dedup;

  // Pass 2: drop vertices whose perpendicular distance to the chord between
  // their neighbors is within tolerance (collinear).
  const out: THREE.Vector2[] = [];
  const n = dedup.length;
  for (let i = 0; i < n; i++) {
    const prev = out.length > 0 ? out[out.length - 1] : dedup[(i - 1 + n) % n];
    const curr = dedup[i];
    const next = dedup[(i + 1) % n];
    const ax = next.x - prev.x;
    const ay = next.y - prev.y;
    const chordLen = Math.hypot(ax, ay);
    if (chordLen < 1e-9) {
      out.push(curr);
      continue;
    }
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    const perp = Math.abs(dx * ay - dy * ax) / chordLen;
    if (perp >= CLEANUP_COLLINEAR_TOLERANCE) out.push(curr);
  }
  return out.length >= 3 ? out : dedup;
}

function pcRingToVec2(ring: PCRing): THREE.Vector2[] {
  const out: THREE.Vector2[] = [];
  for (let i = 0; i < ring.length - 1; i++) out.push(new THREE.Vector2(ring[i][0], ring[i][1]));
  return out;
}

function isCCW(ring: THREE.Vector2[]): boolean {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    s += (b.x - a.x) * (b.y + a.y);
  }
  return s < 0;
}

/**
 * Offset a CCW polygon outward by `t` using per-vertex bisector offsets.
 * Convex corners are mitered (single output vertex along the bisector).
 * Reflex corners and convex corners that exceed the miter limit are beveled
 * (replaced by two vertices, one offset perpendicular to each adjacent edge),
 * which prevents the fold-over that plain bisector offsetting suffers at
 * sharp angles.
 *
 * Output vertex count: same as input for normal corners, +1 per beveled
 * corner. The result is closed under polygon-clipping union with adjacent
 * rooms — shared walls collapse, reducing total vertex count.
 */
function offsetRingOutward(ring: THREE.Vector2[], t: number): THREE.Vector2[] {
  const n = ring.length;
  if (n < 3) return ring.slice();
  if (t <= 0) return ring.slice();

  // Outward edge normals (CCW ring → rotate edge direction 90° CW: (dy, -dx)).
  const edgeNormals: THREE.Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) {
      edgeNormals.push(new THREE.Vector2(0, 0));
    } else {
      edgeNormals.push(new THREE.Vector2(dy / len, -dx / len));
    }
  }

  const out: THREE.Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const v = ring[i];
    const nPrev = edgeNormals[(i - 1 + n) % n]; // normal of edge entering v
    const nNext = edgeNormals[i]; // normal of edge leaving v

    // Bisector = sum of adjacent edge normals; |bisector| = 2 * cos(halfAngle).
    const bx = nPrev.x + nNext.x;
    const by = nPrev.y + nNext.y;
    const bisLen = Math.hypot(bx, by);

    // cross(nPrev, nNext) > 0 means CCW turn = convex corner (CCW ring).
    // cross < 0 means CW turn = reflex corner.
    const cross = nPrev.x * nNext.y - nPrev.y * nNext.x;
    const isReflex = cross < 0;

    if (bisLen < 1e-9) {
      // 180° turn (straight line) — degenerate. Skip.
      continue;
    }

    // Miter offset distance from v along bisector: t / cos(halfAngle).
    // |bisector| / 2 = cos(halfAngle), so offset = t * 2 / bisLen.
    const miterFactor = (2 * t) / bisLen;
    const miterRatio = miterFactor / t; // = 2 / bisLen, dimensionless

    if (!isReflex && miterRatio <= MITER_LIMIT) {
      // Convex corner within miter limit → single mitered vertex.
      out.push(new THREE.Vector2(v.x + (bx / bisLen) * t * miterRatio, v.y + (by / bisLen) * t * miterRatio));
    } else {
      // Reflex corner OR over-mitered convex corner → bevel with two vertices,
      // each offset perpendicular to one of the adjacent edges.
      out.push(new THREE.Vector2(v.x + nPrev.x * t, v.y + nPrev.y * t));
      out.push(new THREE.Vector2(v.x + nNext.x * t, v.y + nNext.y * t));
    }
  }

  return out;
}

function ringToPCRing(ring: THREE.Vector2[]): PCRing {
  const out: PCRing = ring.map((p) => [p.x, p.y] as [number, number]);
  // polygon-clipping requires self-closing rings.
  out.push([out[0][0], out[0][1]]);
  return out;
}
